/* The workflow engine's admin API.
 *
 * ONE BASE AND ONE ERROR SHAPE, shared by the library and the builder.
 * The library previously carried its own copy of both; a second copy in
 * the builder would have been the third place in this repo where a
 * REACT_APP_API_BASE fallback is written out by hand.
 *
 * /api/admin/workflow-engine AND NOT /api/admin/workflows, which is the
 * legacy approval-chain feature (D2). Nothing here touches it.
 */

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:8000'
export const API = `${API_BASE}/api/admin/workflow-engine`

export const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('token')}`,
  'Content-Type': 'application/json',
})

/** An HTTP refusal, carrying the server's own words.
 *
 *  THE BODY IS KEPT, NOT FLATTENED TO A STRING. A blocked publish
 *  returns 422 with a list of findings, each naming the step it is
 *  about, and the builder anchors them to those steps. Reducing the
 *  response to `422 Unprocessable Entity` here would throw away the only
 *  part that tells an author what to fix. */
export class ApiError extends Error {
  constructor(status, body) {
    const detail = body && body.detail
    super(
      (detail && (detail.message || (typeof detail === 'string' ? detail : null))) ||
        `Request failed (${status})`
    )
    this.status = status
    this.body = body
    this.findings = (detail && detail.findings) || null
  }
}

async function request(path, options = {}) {
  let res
  try {
    res = await fetch(`${API}${path}`, { ...options, headers: authHeaders() })
  } catch (e) {
    throw new ApiError(0, null)
  }
  const text = await res.text()
  let body = null
  try {
    body = text ? JSON.parse(text) : null
  } catch {
    body = { detail: text }
  }
  if (!res.ok) throw new ApiError(res.status, body)
  return body
}

export const getMeta = () => request('/meta')

export const listDefinitions = (params) =>
  request(`/definitions?${new URLSearchParams(params)}`)

export const getDefinitionByKey = (key, versionId) =>
  request(
    `/definitions/by-key/${key}` + (versionId ? `?version_id=${versionId}` : '')
  )

export const getDefinition = (id, versionId) =>
  request(`/definitions/${id}` + (versionId ? `?version_id=${versionId}` : ''))

export const saveVersion = (versionId, payload) =>
  request(`/versions/${versionId}`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  })

export const publishVersion = (versionId) =>
  request(`/versions/${versionId}/publish`, { method: 'POST' })

export const setReviewState = (versionId, state) =>
  request(`/versions/${versionId}/review`, {
    method: 'POST',
    body: JSON.stringify({ state }),
  })

export const editPublished = (definitionId) =>
  request(`/definitions/${definitionId}/edit`, { method: 'POST' })

export const getDiff = (versionId, against) =>
  request(
    `/versions/${versionId}/diff` + (against ? `?against=${against}` : '')
  )

/* ── coverage_kind (073) ───────────────────────────────────────────────
 *
 * HOW TO RENDER A coverage_note, DEPENDING ON WHAT KIND OF GAP IT IS.
 *
 * 072 gave definitions a place to record how well they match the code.
 * Reading the sixteen Batch D notes back, they are not all saying the
 * same sort of thing:
 *
 *   #32  "The seed's 'Daily scan runs' was right; this makes it exact."
 *        The flow works. The definition was vague.
 *
 *   #46  "DOCUMENTED AS PROACTIVE, IMPLEMENTED AS AN ON-DEMAND REPORT."
 *        Two of its steps do not happen at all.
 *
 * Both used to render as an identical amber "Known divergence from the
 * code" box. A reviewer who opens #32, finds the note pedantic, and
 * learns to skim the next one will skim #46 — and #46 is the one that
 * embarrasses somebody in a demo.
 *
 * HERE RATHER THAN IN EACH SCREEN, because the library and the builder
 * both draw this and AdminModulesPage.jsx already records in its own
 * header what happens when a vocabulary gets copied per screen: four
 * hardcoded copies with nothing reconciling them.
 *
 * A NULL/UNKNOWN kind falls back to the neutral wording rather than
 * asserting a severity nobody recorded. */
export const COVERAGE_KIND = {
  precision: {
    tone: 'sky',
    title: 'Clarification',
    help: 'The code does what this says. The note makes an imprecise ' +
          'definition exact.',
  },
  divergence: {
    tone: 'amber',
    title: 'Differs from the code',
    help: 'The implementation differs from what this definition ' +
          'describes, but the flow works.',
  },
  product_gap: {
    tone: 'danger',
    title: 'Documented but not built',
    help: 'This definition describes behaviour that does not exist. ' +
          'Read this before demoing the flow.',
  },
}

export const coverageKindOf = (kind) =>
  COVERAGE_KIND[kind] || {
    tone: 'amber',
    title: 'Known divergence from the code',
    help: '',
  }

/* ── SLA suggestions (Batch G) ────────────────────────────────────────
 *
 * 330 steps in the catalogue and sla_hours is NULL on all of them. The
 * review pass has to put a number on the ones that need one, and doing
 * that by hand is how you get 330 plausible round numbers.
 *
 * The backend derives from live configuration and names the source of
 * every value; where nothing covers a step it returns `found: false`
 * with `why_not` rather than a guess. BOTH HALVES MUST BE RENDERED. A
 * screen that shows the suggestions and hides the refusals is claiming a
 * coverage the data does not have — 37 derived against 293 refused, at
 * the time of writing. */
export const fetchSlaSuggestions = (versionId) =>
  request(`/versions/${versionId}/sla-suggestions`)

/* Accepting sends WHICH STEPS, not which values. The server re-derives
 * the numbers from the same function that produced them, so a stale tab
 * or a tampered body cannot write an arbitrary SLA through a route whose
 * name says "suggestions". */
export const applySlaSuggestions = (versionId, stepKeys) =>
  request(`/versions/${versionId}/sla-suggestions/apply`, {
    method: 'POST',
    body: JSON.stringify({
      steps: Object.fromEntries(stepKeys.map((k) => [k, true])),
    }),
  })
